import React, { useEffect, useRef, useState } from 'react'
import cx from 'classnames'
import { TimelineMax, TweenMax } from 'gsap'
import { PrismicRichText } from '@prismicio/react'
import ReactResizeDetector from 'react-resize-detector'
import useNewsTickerItems from 'hooks/useNewsTickerItems'

const NewsTickerSmall = ({ CMS_DATA, width = 0, paused, pauseNewsTicker, resumeNewsTicker, deviceType }) => {
  const tickerRef = useRef(null)
  const { newsItems } = useNewsTickerItems({ CMS_DATA })
  const [currentNewsItemWidth, setCurrentNewsItemWidth] = useState(0)
  const [completed, setCompleted] = useState(true)
  const [hidden, setHidden] = useState(true)
  const [tl] = useState(new TimelineMax({ paused: true }))
  const [duration, setDuration] = useState(0)

  useEffect(() => {
    setDuration(Math.round(currentNewsItemWidth / 100))
  }, [currentNewsItemWidth])

  useEffect(() => {
    if (duration && currentNewsItemWidth && completed && !!width) {
      tl.kill() // garbage collection
      tl.fromTo(
        tickerRef.current,
        { x: width },
        {
          x: -currentNewsItemWidth,
          duration,
          delay: 0,
          ease: 'none',
          repeat: 0,
          onStart: () => setCompleted(false),
          onComplete: () => setCompleted(true)
        }
      ).play()
      tl.progress(0)
    }
  }, [currentNewsItemWidth, duration, completed, width])

  useEffect(() => {
    return () => {
      // garbage collection on unmount
      tl.kill()
    }
  }, [])

  useEffect(() => {
    if (currentNewsItemWidth && width && duration) {
      setHidden(false)
    }
  }, [currentNewsItemWidth, width, duration, setHidden])

  useEffect(() => {
    if (paused !== null) {
      if (paused) {
        TweenMax.delayedCall(0.1, () => tl.pause())
      } else {
        TweenMax.delayedCall(0.1, () => tl.resume())
      }
    }
  }, [tl, paused])

  if (!newsItems) return null

  return (
    <div
      onClick={() => paused ? resumeNewsTicker() : pauseNewsTicker()}
      onMouseEnter={() => !deviceType.isMobile && pauseNewsTicker()}
      onMouseLeave={() => !deviceType.isMobile && resumeNewsTicker()}
    >
      <div
        className={cx(
          'absolute flex items-center h-100',
          { 'w-resize': paused }
        )}
      >
        <div
          className={cx(
            'flex no-wrap shrink-0 grow-0 new-ticker overflow-hidden',
            { hidden },
            { 'no-pointer-events ds': !paused }
          )}
          data-testid='small-news-ticker'
        >
          <ReactResizeDetector handleWidth>
            <ResizeNewsItem {...{ tickerRef, currentNewsItems: newsItems, setCurrentNewsItemWidth }} />
          </ReactResizeDetector>
        </div>
      </div>
    </div>
  )
}

const ResizeNewsItem = ({ tickerRef, currentNewsItems, setCurrentNewsItemWidth, width }) => {
  useEffect(() => {
    setCurrentNewsItemWidth(width)
  }, [width, setCurrentNewsItemWidth])

  return (
    <div ref={tickerRef} className='flex grow-0 shrink-0'>
      <div className='overflow-hidden flex'>
        {currentNewsItems.map((item, idx) => (
          <div className='flex mh6 items-center no-wrap shrink-0 grow-0' key={idx} data-testid='small-news-item'>
            <PrismicRichText field={item} />
          </div>
        ))}
      </div>
    </div>
  )
}

export default NewsTickerSmall
